import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Search } from 'lucide-react';
import { MdPrint } from "react-icons/md"; // Import print icon
import classes from "./ManageRegion.module.css"
import plus from "../../../../Assets/plus.svg"
import EyeIcon from "../../../../Assets/new-eye.svg"
import DeleteIcon from "../../../../Assets/new-delete.svg"
import { Spinner, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import HeaderNav from "../../../Budget/Budget Dashboard/HeaderNav/HeaderNav";
import axios from "axios";
import { BASE_URL } from "../../../../Components/Styles/API/API";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const ManageRegion = () => {
  // State declarations
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [regionDescription, setRegionDescription] = useState("");
  const [selectedContinent, setSelectedContinent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [continents, setContinents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [bearer, setBearer] = useState('');
  const [tableData12, setTableData12] = useState([]);
  const [tableData44, setTableData44] = useState([]);
  const [loads, setLoads] = useState(false);
  const [selectedContinentFilter, setSelectedContinentFilter] = useState(null);
  const navigate = useNavigate();

  // Select styles with proper font size
  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: '100%',
      fontSize: '14px', // Proper font size
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      fontSize: '14px', // Proper font size
      border: '1px solid #ced4da',
      '&:hover': {
        borderColor: '#80bdff',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '14px', // Proper font size
      color: '#495057',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px', // Proper font size
      backgroundColor: state.isFocused ? "#e9ecef" : "white",
      color: "#495057",
      '&:active': {
        backgroundColor: '#007bff',
        color: 'white'
      }
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: "300px",
      overflowY: "auto",
      fontSize: '14px', // Proper font size
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#6c757d',
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#495057',
    }),
  };

  // Print View handler
  const handlePrintView = () => {
    navigate('/budget/charts_of_account/Region');
  };

  const readData = async () => {
    try {
      const value = await localStorage.getItem('userToken');
      if (value !== null) {
        setBearer(value);
      }
    } catch (e) {
      console.error('Failed to fetch token from storage');
      setError("Authentication error. Please log in again.");
    }
  };

  useEffect(() => {
    readData();
  }, []);

  // Headers function to ensure fresh token
  const getHeaders = () => {
    const token = bearer || localStorage.getItem('userToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fixed helper function to extract data from API response
  const extractDataFromResponse = (response) => {
    if (!response || !response.data) return [];

    const data = response.data;

    console.log('API Response Structure:', data);

    // Handle the specific structure: {status: true, message: "...", data: {...}}
    if (data.status && data.data) {
      // If data.data is an array, return it directly
      if (Array.isArray(data.data)) {
        return data.data;
      }
      // If data.data is an object with regions/items property
      else if (data.data.regions && Array.isArray(data.data.regions)) {
        return data.data.regions;
      }
      else if (data.data.items && Array.isArray(data.data.items)) {
        return data.data.items;
      }
      // If data.data is an object with array inside
      else {
        // Try to find any array property in data.data
        const arrayKeys = Object.keys(data.data).filter(key => Array.isArray(data.data[key]));
        if (arrayKeys.length > 0) {
          return data.data[arrayKeys[0]];
        }
      }
    }

    // Fallback to previous logic for other structures
    if (Array.isArray(data)) {
      return data;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else if (data.regions && Array.isArray(data.regions)) {
      return data.regions;
    } else if (data.items && Array.isArray(data.items)) {
      return data.items;
    } else if (typeof data === 'object') {
      return [data];
    } else {
      console.warn('Unexpected API response structure:', data);
      return [];
    }
  };

  // Fetch regions from API
  const fetchRegions = async () => {
    try {
      setLoading(true);
      setError("");

      if (!bearer) {
        setError("Please log in to access this resource");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BASE_URL}/regions`, { headers: getHeaders() });
      console.log('Full Regions API Response:', response);

      const regionsData = extractDataFromResponse(response);
      console.log('Extracted Regions Data:', regionsData);

      setRegions(regionsData);
      setTableData44(regionsData); // Initialize tableData44 with all regions

      if (regionsData.length === 0) {
        setSuccess("No regions found. Create your first region.");
      } else {
        setSuccess("Regions loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      handleApiError(error, "load regions");
      setRegions([]);
      setTableData44([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContinents = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/continents`, { headers: getHeaders() });

      const results = response.data?.data;

      const options = results.map((item) => ({
        label: item.description,
        value: item.id,
      }));

      // Add "All" option to continents
      const continentsWithAll = [
        { label: "All Continents", value: "all" },
        ...options
      ];

      setTableData12(continentsWithAll);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else {
        let errorMessage = 'An error occurred. Please try again.';
        if (error.response && error.response.data && error.response.data.message) {
          if (typeof error.response.data.message === 'string') {
            errorMessage = error.response.data.message;
          } else if (Array.isArray(error.response.data.message)) {
            errorMessage = error.response.data.message.join('; ');
          } else if (typeof error.response.data.message === 'object') {
            errorMessage = JSON.stringify(error.response.data.message);
          }
        }
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch regions by continent using the filter API
  const fetchRegionsByContinent = async (continentId) => {
    if (!continentId || continentId === 'all') {
      // If "All" is selected or no continent, show all regions
      fetchRegions();
      return;
    }

    setLoads(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/regions/filter-by-continent?continent_id=${continentId}`,
        { headers: getHeaders() }
      );

      console.log('Filtered Regions API Response:', response);
      const filteredRegions = extractDataFromResponse(response);

      if (filteredRegions && Array.isArray(filteredRegions)) {
        setTableData44(filteredRegions);
        setSuccess(`Loaded ${filteredRegions.length} regions for the selected continent`);
      } else {
        setTableData44([]);
        setSuccess("No regions found for the selected continent");
      }
    } catch (error) {
      console.error("Error fetching filtered regions:", error);
      handleApiError(error, "load regions for continent");
      setTableData44([]);
    } finally {
      setLoads(false);
    }
  };

  useEffect(() => {
    if (bearer) {
      fetchRegions();
      fetchContinents();
    }
  }, [bearer]);

  // Auto-fetch regions when continent filter changes
  useEffect(() => {
    if (bearer && selectedContinentFilter) {
      fetchRegionsByContinent(selectedContinentFilter.value);
    } else if (bearer && !selectedContinentFilter) {
      // If no continent filter, show all regions
      fetchRegions();
    }
  }, [selectedContinentFilter, bearer]);

  // Generic error handler
  const handleApiError = (error, action = "perform this action") => {
    console.error(`Error trying to ${action}:`, error);

    if (error.response?.status === 401) {
      setError("Session expired. Please log in again.");
      navigate('/login');
    } else if (error.response?.status === 404) {
      setError("Resource not found. Please check the API configuration.");
    } else if (error.response?.data?.message) {
      const errorMessage = error.response.data.message;
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } else if (error.message) {
      setError(error.message);
    } else {
      setError(`Failed to ${action}. Please try again.`);
    }
  };

  // Reset form states
  const resetForm = () => {
    setRegionDescription("");
    setSelectedContinent(null);
    setSelectedRegion(null);
    setError("");
  };

  // Create new region - FIXED with correct endpoint
  const createRegion = async () => {
    if (!regionDescription.trim()) {
      setError("Please enter region description");
      return;
    }

    if (!selectedContinent) {
      setError("Please select a continent");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to create this region?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Create",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const payload = {
            description: regionDescription.trim(),
            continent_id: selectedContinent.value
          };

          // Use the correct create endpoint
          const response = await axios.post(`${BASE_URL}/regions/create_region`, payload, {
            headers: getHeaders()
          });

          if (response.data.status || response.data.success) {
            setSuccess("Region created successfully");
            resetForm();
            setShowCreateModal(false);
            fetchRegions(); // Refresh the list

            Swal.fire({
              title: "Success!",
              text: response.data.message || "Region created successfully",
              icon: "success",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          handleApiError(error, "create region");

          Swal.fire({
            title: "Error!",
            text: error.response?.data?.message || "Failed to create region",
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  // Update region - FIXED with correct endpoint
  const updateRegion = async () => {
    if (!regionDescription.trim()) {
      setError("Please enter region description");
      return;
    }

    if (!selectedContinent) {
      setError("Please select a continent");
      return;
    }

    if (!selectedRegion?.id) {
      setError("No region selected for update");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update this region?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const payload = {
            id: selectedRegion.id,
            description: regionDescription.trim(),
            continent_id: selectedContinent.value
          };

          // Use the correct update endpoint
          const response = await axios.post(`${BASE_URL}/regions/update_region`, payload, {
            headers: getHeaders()
          });

          if (response.data.status || response.data.success) {
            setSuccess("Region updated successfully");
            setShowEditModal(false);
            resetForm();
            fetchRegions(); // Refresh the list

            Swal.fire({
              title: "Success!",
              text: response.data.message || "Region updated successfully",
              icon: "success",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          handleApiError(error, "update region");

          Swal.fire({
            title: "Error!",
            text: error.response?.data?.message || "Failed to update region",
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const deleteRegion = async (regionId) => {
    if (!regionId) {
      setError("No region selected for deletion");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Show loading state immediately
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while we delete the region',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          // Use GET request with query parameters for delete
          const response = await axios.get(
            `${BASE_URL}/regions/delete_region`,
            {
              params: { id: regionId },
              headers: getHeaders()
            }
          );

          if (response.data.status || response.data.success) {
            setSuccess("Region deleted successfully");
            fetchRegions(); // Refresh the list

            Swal.fire({
              title: "Deleted!",
              text: response.data.message || "Region deleted successfully",
              icon: "success",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting region:", error);
          let errorMessage = "Something went wrong while deleting the region.";

          if (error.response?.status === 401) {
            errorMessage = "Session expired. Please log in again.";
            localStorage.removeItem('userToken');
            navigate('/login');
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }

          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // Handle edit region
  const handleEditRegion = (region) => {
    if (!region) return;

    setSelectedRegion(region);
    setRegionDescription(region.description || '');

    // Set the continent for the selected region
    if (region.continent) {
      setSelectedContinent({
        value: region.continent.id,
        label: region.continent.description
      });
    } else if (region.continent_id) {
      // If only continent_id is available, find the continent object
      const continent = tableData12.find(c => c.value === region.continent_id);
      if (continent) {
        setSelectedContinent(continent);
      } else {
        setSelectedContinent(null);
      }
    } else {
      setSelectedContinent(null);
    }

    setShowEditModal(true);
    setError("");
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetForm();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
    setError("");
  };

  // Handle continent filter change
  const handleContinentFilter = (selectedOption) => {
    setSelectedContinentFilter(selectedOption);
  };

  // Filter regions based on search term
  const filteredRegions = tableData44.filter(region => {
    if (!region) return false;

    const regionDesc = region.description || '';
    const continentDesc = region.continent?.description || '';
    const regionId = region.id ? region.id.toString() : '';

    return (
      regionDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      continentDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      regionId.includes(searchTerm)
    );
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Prepare continent options for Select component
  const continentOptions = tableData12;

  // Skeleton loader component
  const SkeletonLoader = () => {
    return (
      <>
        {Array.from({ length: 7 }).map((_, index) => (
          <tr key={index}>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '30px', height: '20px' }}></div>
            </td>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '120px', height: '20px' }}></div>
            </td>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '100px', height: '20px' }}></div>
            </td>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '80px', height: '20px' }}></div>
            </td>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '80px', height: '20px' }}></div>
            </td>
            <td className={classes.textby}>
              <div className={classes.skeleton} style={{ width: '80px', height: '20px' }}></div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <div>
      <HeaderNav />

      {/* Create Region Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Create Region</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Region Description *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Region Description"
                value={regionDescription}
                onChange={(e) => setRegionDescription(e.target.value)}
                isInvalid={!regionDescription.trim()}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a region description
              </Form.Control.Feedback>

              <Form.Label style={{ marginTop: '20px' }}>Continent *</Form.Label>
              <Select
                options={continentOptions}
                value={selectedContinent}
                onChange={setSelectedContinent}
                placeholder="Select Continent"
                isClearable
                styles={selectStyles} // Using the proper font styles
              />
              {!selectedContinent && (
                <div className="text-danger small mt-1">Please select a continent</div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancel
          </Button>
          <Button
            className={classes.customButton}
            onClick={createRegion}
            disabled={isLoading || !regionDescription.trim() || !selectedContinent}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" animation="border" />
                <span style={{ marginLeft: "5px" }}>Creating...</span>
              </>
            ) : (
              "Create Region"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update Region Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Update Region</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Region Description *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Region Description"
                value={regionDescription}
                onChange={(e) => setRegionDescription(e.target.value)}
                isInvalid={!regionDescription.trim()}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a region description
              </Form.Control.Feedback>

              <Form.Label style={{ marginTop: '20px' }}>Continent *</Form.Label>
              <Select
                options={continentOptions}
                value={selectedContinent}
                onChange={setSelectedContinent}
                placeholder="Select Continent"
                isClearable
                styles={selectStyles} // Using the proper font styles
              />
              {!selectedContinent && (
                <div className="text-danger small mt-1">Please select a continent</div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button
            className={classes.customButton}
            onClick={updateRegion}
            disabled={isLoading || !regionDescription.trim() || !selectedContinent}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" animation="border" />
                <span style={{ marginLeft: "5px" }}>Updating...</span>
              </>
            ) : (
              "Update Region"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className={classes.topContainer}>
        <div className={classes.topNavBars}>
          <Button className={classes.buttonsNav}>Remittances</Button>
          <Button className={classes.buttonsNav}>Petty Cash</Button>
          <Button className={classes.buttonsNav}>Supplier's Payment</Button>
          <Button className={classes.buttonsNav}>Customer's Ledger</Button>
        </div>

        <div style={{ marginTop: 24 }} />
        <h3 className={classes.topText}>Manage Region</h3>
        <p className={classes.prgText}>Add, View, Edit, and Delete Region.</p>

        <div className={classes.subContainer} style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
          <div style={{ minWidth: '200px' }}>
            <label className={classes.filterLabel}>Continent</label>
            <Select
              onChange={handleContinentFilter}
              options={tableData12}
              value={selectedContinentFilter}
              menuPortalTarget={document.body}
              styles={selectStyles}
              placeholder="Select Continent"
              isClearable
              className={classes.filterSelect}
            />
          </div>

          {/* Middle: Total Regions */}
          <div className={classes.statCount} style={{ flexShrink: 0, margin: '0 auto', marginTop: '18px' }}>
            <div className={classes.statConn}>
              <p className={classes.txtHd}>Total Regions</p>
              {loading ? (
                <div className={classes.skeleton} style={{ width: '40px', height: '24px', marginTop: '-10px' }}></div>
              ) : (
                <p className={classes.txtHds}>
                  {filteredRegions.length}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Search, Print View, and Add New Region */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexShrink: 0, marginTop: '18px' }}>
            {/* Search Input */}
            <div className={classes.searchInputWrapper}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchInput}
                placeholder="Search regions "
              />
              <Search className={classes.searchIcon} size={16} />
            </div>

            {/* Print View Button */}
            <div>
              <button className={classes.loanBtn} onClick={handlePrintView}>
                <MdPrint size={18} style={{ marginRight: '8px' }} />
                Print View
              </button>
            </div>

            {/* Add New Region Button */}
            <div>
              <button className={classes.loanBtn} onClick={handleShowCreateModal}>
                <img src={plus} alt="plus" /> Add New Region
              </button>
            </div>
          </div>
        </div>

        <div className={classes.tableContainer}>
          <table className={classes.tableTable}>
            <thead className={classes.tableHeader}>
              <tr>
                <th>#</th>
                <th>Region</th>
                <th>Continent</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonLoader />
              ) : filteredRegions.length > 0 ? (
                filteredRegions.map((region, index) => (
                  <tr key={region.id || index}>
                    <td className={classes.textby}>{index + 1}</td>
                    <td className={classes.textby}>
                      <span className={classes.regionBadge}>
                        {region.description || 'N/A'}
                      </span>
                    </td>
                    <td className={classes.textby}>
                      <span className={classes.statucColor}>
                        {region.continent?.description || 'N/A'}
                      </span>
                    </td>
                    <td className={classes.textby}>
                      {formatDate(region.created_at)}
                    </td>
                    <td className={classes.textby}>
                      {formatDate(region.updated_at)}
                    </td>
                    <td className={classes.btnAction}>
                      <button
                        className={classes.actionBtn}
                        onClick={() => handleEditRegion(region)}
                        title="Edit Region"
                      >
                        <img src={EyeIcon} alt="Edit" />
                      </button>
                      <button
                        className={classes.actionBtn}
                        onClick={() => deleteRegion(region.id)}
                        title="Delete Region"
                      >
                        <img src={DeleteIcon} alt="Delete" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center p-4">
                    {loading ? "Loading regions..." :
                      searchTerm ? "No regions found matching your search criteria" :
                        "No regions available. Create your first region!"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && filteredRegions.length > 0 && (
            <div className={classes.paginationStyle}>
              <div className={classes.footerSubOne}>
                <button className={classes.btnFirst} disabled>First</button>
                <span className={classes.ftOne}>
                  <button className={`${classes.pageNumber} ${classes.active}`}>1</button>
                </span>
                <button className={classes.btnSec} disabled>Last</button>
              </div>
              <p className={classes.displayPag}>
                Displaying 1-{filteredRegions.length} of {filteredRegions.length}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ManageRegion;